/**
 * Calendar.js — V16 (header injection, bottom border preserved, title fix)
 * Fixes: A1/L1 header text size, D1 title centering, bottom border row preserved
 */

import React, { useEffect, useState, useRef } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from "@fullcalendar/interaction";
import firebaseDb from "../firebase-config";
import { useParams, useNavigate } from 'react-router-dom';
import '../App.css';
import AddEditEvent from './AddEditEvent';
import { IconButton, Snackbar, Alert, Button } from "@mui/material";
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import DownloadIcon from '@mui/icons-material/Download';
import { getHolidaysForMonth } from '../holidays.js';

const DATE_COL = ['A','C','E','G','I','K','M'];
const HOL_COL  = ['B','D','F','H','J','L','N'];
const DATE_S   = [2, 4, 4, 4, 4, 4, 2];
const HOL_S    = [3, 5, 5, 5, 5, 5, 3];
const CONT_S   = [26, 28, 28, 28, 28, 28, 26];
const CONT_HS  = [27, 27, 27, 27, 27, 27, 27];
const DATE_ROW_START = 3;
const ROWS_PER_WEEK  = 6;
const CONTENT_ROWS   = 5;

function sleep(ms) { return new Promise(r => setTimeout(r, ms)); }

function Calendar({ showSidebar, setShowSidebar }) {
    const navigate = useNavigate();
    const [openForm, setOpenForm]             = useState(false);
    const [copied, setCopied]                 = useState(false);
    const [isCopying, setIsCopying]           = useState(false);
    const [formType, setFormType]             = useState("Add");
    const [currentEvent, setCurrentEvent]     = useState({});
    const [hoveredDay, setHoveredDay]         = useState(null);
    const [scrollPosition, setScrollPosition] = useState(0);
    const [events, setEvents]                 = useState([]);
    const { venue } = useParams();
    const calendarRef = useRef(null);

    useEffect(() => {
        firebaseDb.child('database/events').orderByChild('venue').equalTo(venue)
            .on('value', (snapshot) => {
                const obj = snapshot.val();
                if (!obj) return;
                setEvents(Object.keys(obj).map(key => ({
                    title: obj[key].stage, start: obj[key].start, end: obj[key].end,
                    extendedProps: {
                        date: obj[key].date, stage: obj[key].stage, performers: obj[key].performers,
                        startTime: obj[key].startTime, email: obj[key].email, endTime: obj[key].endTime,
                        price: obj[key].price, venue: obj[key].venue, eventID: key,
                    },
                })));
            });
    }, [venue]);

    useEffect(() => {
        if (openForm) {
            setScrollPosition(window.scrollY);
            document.body.style.position = 'fixed';
            document.body.style.top = `-${scrollPosition}px`;
            document.body.style.width = '100%';
        } else {
            document.body.style.position = '';
            document.body.style.top = '';
            window.scrollTo({ top: scrollPosition, behavior: 'smooth' });
        }
        return () => { document.body.style.position = ''; document.body.style.top = ''; };
    }, [openForm, scrollPosition]);

    const handleDateClick = (arg) => {
        if (isCopying) return;
        setScrollPosition(window.scrollY);
        setCurrentEvent({ date: arg.dateStr, venue });
        setFormType("Add"); setOpenForm(true);
        sleep(200).then(() => document.getElementById("client")?.focus());
    };

    const handleEventClick = (info) => {
        setScrollPosition(window.scrollY);
        setCurrentEvent({
            stage: info.event.extendedProps.stage, performers: info.event.extendedProps.performers,
            startTime: info.event.extendedProps.startTime, endTime: info.event.extendedProps.endTime,
            email: info.event.extendedProps.email, price: info.event.extendedProps.price,
            date: info.event.extendedProps.date, eventID: info.event.extendedProps.eventID,
            venue: info.event.extendedProps.venue, start: info.event.startStr, end: info.event.endStr,
        });
        setFormType("Edit"); setOpenForm(true);
        sleep(200).then(() => document.getElementById("client")?.focus());
    };

    const handleCopy = async (event, formattedDate) => {
        setIsCopying(true);
        const eventsOnDay = events
            .filter(o => o.extendedProps.date === formattedDate && o.extendedProps.stage)
            .sort((a, b) => (a.extendedProps.startTime ?? "").substring(0,2) - (b.extendedProps.startTime ?? "").substring(0,2));
        let copyText = "";
        eventsOnDay.forEach(e => {
            const start = normalizeTimeStr(new Date(e.start).toLocaleTimeString([], { hour:'2-digit', minute:'2-digit' }));
            const end   = normalizeTimeStr(new Date(e.end).toLocaleTimeString([], { hour:'2-digit', minute:'2-digit' }));
            copyText += `${e.extendedProps.stage}\n${to12HourFormat(start)} to ${to12HourFormat(end)}\n`;
        });
        setTimeout(async () => { await navigator.clipboard.writeText(copyText); setCopied(true); }, 100);
        await sleep(1000);
        setIsCopying(false);
    };

    const handleCopiedClosed = (event, reason) => { if (reason !== 'clickaway') setCopied(false); };

    const renderCopyButton = (content) => {
        const date = content.date;
        const fd   = [
            date.toLocaleString("default", { year: "numeric" }),
            date.toLocaleString("default", { month: "2-digit" }),
            date.toLocaleString("default", { day: "2-digit" }),
        ].join("-");
        return (
            <div onMouseEnter={() => setHoveredDay(fd)} onMouseLeave={() => setHoveredDay(null)}
                style={{ display:"flex", height:"100%", width:"100%", alignItems:"center", justifyContent:"flex-end", position:'relative' }}>
                <IconButton id={fd} onClick={e => handleCopy(e, fd)} aria-label="copy"
                    style={{ padding:"3px", visibility: hoveredDay===fd ? 'visible':'hidden', position:'absolute', right:'8px', top:'50%', transform:'translateY(-50%)' }}>
                    <ContentCopyIcon />
                </IconButton>
                <span style={{ marginLeft:"20px" }}>{content.dayNumberText}</span>
            </div>
        );
    };

    function formatEventTitle(title) {
        if (!title) return title;
        if (title.toLowerCase().includes(' with ')) {
            const idx = title.toLowerCase().indexOf(' with ');
            return (<><span style={{display:'block'}}>{title.substring(0,idx)}</span><span style={{display:'block'}}>{title.substring(idx+1)}</span></>);
        }
        if (title.includes(' & ') && title.length > 15) {
            return title.split(' & ').map((p,i) => <span key={i} style={{display:'block'}}>{i>0?'& ':''}{p}</span>);
        }
        return title;
    }

    function to12HourFormat(t) {
        if (!t) return "";
        const [h, m] = t.split(":");
        if (!m) return "";
        return `${parseInt(h,10) % 12 || 12}:${m}`;
    }
    function normalizeTimeStr(s) { return s.split(" ")[0].replace(/AM|PM/g,"").trim(); }
    function cleanName(n)        { return n ? n.trim().replace(/\s+/g," ") : ""; }

    async function getEventsForDayMap(dateStr, targetVenue) {
        const snap = await firebaseDb.child('database/events').orderByChild('date').equalTo(dateStr).get();
        const val  = snap.val() || {};
        const arr  = Object.keys(val)
            .map(k => ({ start: val[k].start, end: val[k].end, venue: val[k].venue, stageName: cleanName(val[k].stage) }))
            .filter(e => e.venue === targetVenue)
            .sort((a,b) => a.start.localeCompare(b.start));
        const map = new Map();
        for (const e of arr) map.set(`${e.start.substring(11)} - ${e.end.substring(11)}`, e.stageName);
        return map;
    }

    function formatTimeRange(tk) {
        const [s,e] = tk.split(" - ");
        const trim  = t => t.length > 5 ? t.substring(0,5) : t;
        return `${to12HourFormat(trim(s))} - ${to12HourFormat(trim(e))}`;
    }

    // Insert line breaks at the correct word-wrap points for long artist names.
    function shortenForCell(name) {
        if (!name) return name;
        // Each replacement puts a \n at the exact wrap point shown in the reference calendar.
        return name
            .replace(/^Eric Perkins & Friends$/i,       'Eric Perkins\n& Friends')
            .replace(/^Frank Jordon & Friends$/i,        'Frank Jordon\n& Friends')
            .replace(/^John Bull & Friends$/i,           'John Bull\n& Friends')
            .replace(/^Scott Grant & Sam Williams$/i,    'Scott Grant\n& Sam Williams')
            .replace(/^Joshua Stephen Ward$/i,           'Joshua Stephen\nWard')
            .replace(/^Karaoke with Elaine$/i,           'Karaoke\nwith Elaine')
            .replace(/^Karaoke with Jimmie$/i,           'Karaoke\nwith Jimmie')
            // Generic fallback for any other "X & Friends" not listed above
            .replace(/ & Friends$/i,                     '\n& Friends')
            .replace(/ & Band$/i,                        '\n& Band');
    }

    function xmlEsc(str) {
        return String(str)
            .replace(/&/g,'&amp;').replace(/</g,'&lt;')
            .replace(/>/g,'&gt;').replace(/"/g,'&quot;').replace(/'/g,'&apos;');
    }

    function inlineCell(addr, styleIdx, text) {
        if (text === null || text === undefined || text === '') {
            return `<c r="${addr}" s="${styleIdx}" t="n"></c>`;
        }
        return `<c r="${addr}" s="${styleIdx}" t="inlineStr"><is><t xml:space="preserve">${xmlEsc(text)}</t></is></c>`;
    }

    function numCell(addr, styleIdx, num) {
        return `<c r="${addr}" s="${styleIdx}" t="inlineStr"><is><t>${num}</t></is></c>`;
    }

    function makeDateRow(rowNum, datesByDow) {
        let cells = '';
        for (let dow = 0; dow < 7; dow++) {
            const dc   = DATE_COL[dow] + rowNum;
            const hc   = HOL_COL[dow]  + rowNum;
            const info = datesByDow.get(dow);
            if (info) {
                cells += numCell(dc, DATE_S[dow], info.dateNum);
                cells += inlineCell(hc, HOL_S[dow], info.holiday || '');
            } else {
                cells += `<c r="${dc}" s="${DATE_S[dow]}" t="n"></c>`;
                cells += `<c r="${hc}" s="${HOL_S[dow]}" t="n"></c>`;
            }
        }
        return `<row r="${rowNum}" ht="24" customFormat="1" customHeight="1" s="1">${cells}</row>`;
    }

    // Border styles for the last content row of each week (thick bottom border)
    const BORD_S  = [29, 30, 31, 30, 31, 30, 29];
    const BORD_HS = [30, 30, 30, 30, 30, 30, 30];

    function makeContentRow(rowNum, linesByDow, isBorderRow = false) {
        const cs = isBorderRow ? BORD_S  : CONT_S;
        const hs = isBorderRow ? BORD_HS : CONT_HS;
        let cells = '';
        for (let dow = 0; dow < 7; dow++) {
            const dc   = DATE_COL[dow] + rowNum;
            const hc   = HOL_COL[dow]  + rowNum;
            const line = linesByDow.get(dow) || '';
            cells += inlineCell(dc, cs[dow], line);
            cells += `<c r="${hc}" s="${hs[dow]}" t="n"></c>`;
        }
        const rowStyle = isBorderRow ? ' s="6"' : ' s="1"';
        return `<row r="${rowNum}" ht="16" customFormat="1" customHeight="1"${rowStyle}>${cells}</row>`;
    }

    function formatTimeRangeODS(tk) {
        const [s, e] = tk.split(" - ");
        const trim = t => t.length > 5 ? t.substring(0, 5) : t;
        return `${to12HourFormat(trim(s))} to ${to12HourFormat(trim(e))}`;
    }

    async function generateODS() {
        // ── 1. Get current month/year from the calendar view ─────────────────
        const titleEl = document.getElementsByClassName("fc-toolbar-title")[0];
        if (!titleEl) { alert('Could not read calendar month.'); return; }
        const titleText = titleEl.textContent.trim();
        const parsed    = new Date(Date.parse(titleText + ' 1'));
        if (isNaN(parsed)) { alert('Could not parse calendar month: ' + titleText); return; }
        const year      = parsed.getFullYear();
        const month     = parsed.getMonth(); // 0-indexed
        const monthName = parsed.toLocaleString('default', { month: 'long' });

        // ── 2. Fetch blank Vertex42 ODS template ─────────────────────────────
        const resp = await fetch(`/CalendarTemplateVertex.ods?v=${Date.now()}`);
        if (!resp.ok) { alert('Could not load ODS template.'); return; }
        const buf  = await resp.arrayBuffer();
        const JSZip = (await import('jszip')).default;
        const zip   = await JSZip.loadAsync(buf);
        let content = await zip.file('content.xml').async('string');
        let manifest = await zip.file('META-INF/manifest.xml').async('string');

        // The "Live Music Nightly On The Patio" header text (flanking the
        // month title) is now rendered as native ODF text styled to match
        // the title's own color/font — no PNG images or fetches needed.

        // ── 3. Find this month's start row in the template ───────────────────
        // Month headers appear every 41 rows starting at row 1.
        // Row offsets (1-based): Jan=1, Feb=37, Mar=78 ... (gap varies slightly)
        // Use regex to find the exact position of "MONTHNAME YEAR" in the XML.
        const monthUpper = monthName.toUpperCase();
        // The ODS template inserts an ODF <text:s/> spacing tag between the month
        // name and the year (e.g. "SEPTEMBER <text:s/>2026"), so the search must
        // tolerate optional whitespace and/or XML tags in between, not just spaces.
        const monthRegex = new RegExp(`${monthUpper}(?:\\s|<[^>]*>)*${year}`);
        if (!monthRegex.test(content)) {
            alert(`Could not find ${monthUpper} ${year} in the template. Make sure the template covers this year.`);
            return;
        }

        // ── 4. Build booking data for each day of the month ──────────────────
        const firstDow = new Date(year, month, 1).getDay(); // 0=Sun
        const lastDay  = new Date(year, month + 1, 0).getDate();
        const holidays = getHolidaysForMonth(year, month + 1);

        // weekData[weekIndex][dow] = [line1, line2, line3, line4, line5]
        const weekData = [];
        let dateCounter = 1;

        for (let w = 0; w < 6; w++) {
            const week = {};
            for (let dow = 0; dow < 7; dow++) {
                const isLeading  = w === 0 && dow < firstDow;
                const isTrailing = dateCounter > lastDay;
                if (isLeading || isTrailing) { week[dow] = { dateNum: null, lines: [] }; continue; }

                const mm       = String(month + 1).padStart(2, '0');
                const dd       = String(dateCounter).padStart(2, '0');
                const fulldate = `${year}-${mm}-${dd}`;
                const holiday  = holidays.get(fulldate)?.[0] ?? '';
                const eventsMap = await getEventsForDayMap(fulldate, venue);

                const lines = [];
                for (const [tk, name] of eventsMap.entries()) {
                    const cellName = shortenForCell(cleanName(name));
                    if (cellName.includes('\n')) {
                        const parts = cellName.split('\n');
                        lines.push(parts[0]);
                        lines.push(parts[1]);
                    } else {
                        lines.push(cellName);
                    }
                    lines.push(formatTimeRangeODS(tk));
                }

                week[dow] = { dateNum: dateCounter, holiday, lines };
                dateCounter++;
            }
            weekData.push(week);
            if (Object.values(week).every(d => d.dateNum === null)) break;
        }

        // ── 5. Inject booking data into the ODS XML ──────────────────────────
        // The ODS uses <table:table-row> elements. We need to:
        // (a) Find the month section
        // (b) Replace the content rows for each week with our data
        //
        // Strategy: split content into rows, find month start, rebuild week rows.

        // Split into row chunks for manipulation
        const rowPattern = /(<table:table-row[^>]*>[\s\S]*?<\/table:table-row>)/g;
        const allRows = content.match(rowPattern);
        if (!allRows) { alert('Could not parse ODS rows.'); return; }

        // Find the row index where this month starts
        let monthRowIdx = -1;
        for (let i = 0; i < allRows.length; i++) {
            if (monthRegex.test(allRows[i])) {
                monthRowIdx = i;
                break;
            }
        }
        if (monthRowIdx === -1) { alert('Could not locate month in template rows.'); return; }

        // ── Build a lookup of the template's cell styles, and a helper that ──
        // derives a "filled" version of any blank-cell style: same borders
        // (which vary by grid position), but centered/13pt/bold text — matching
        // how correctly-formatted, already-filled reference calendars style
        // their content cells (vs. the blank template's left-aligned/10pt default).
        const styleBlocks = {};
        const styleBlockRe = /<style:style style:name="(ce\d+)" style:family="table-cell"[^>]*>[\s\S]*?<\/style:style>/g;
        let sbm;
        while ((sbm = styleBlockRe.exec(content)) !== null) {
            styleBlocks[sbm[1]] = sbm[0];
        }
        const filledStyleCache = {};
        const newStyleDefs = [];
        let filledStyleCounter = 0;

        function makeFilledStyle(origBlock, newName) {
            const cellPropsMatch = origBlock.match(/<style:table-cell-properties[^>]*\/>/);
            const cellProps = cellPropsMatch ? cellPropsMatch[0] : '<style:table-cell-properties/>';
            return `<style:style style:name="${newName}" style:family="table-cell" style:parent-style-name="Excel_20_Built-in_20_Normal">${cellProps}<style:paragraph-properties fo:text-align="center" fo:margin-left="0in" style:writing-mode="page"/><style:text-properties style:use-window-font-color="true" style:text-outline="false" style:text-line-through-style="none" style:font-name="Arial" fo:font-size="13pt" fo:font-style="normal" fo:text-shadow="none" style:text-underline-style="none" fo:font-weight="bold" style:font-size-asian="13pt" style:font-style-asian="normal" style:font-weight-asian="bold" style:font-name-complex="Arial" style:font-size-complex="13pt" style:font-style-complex="normal" style:font-weight-complex="bold"/></style:style>`;
        }

        function getFilledStyleName(origStyleName) {
            if (filledStyleCache[origStyleName]) return filledStyleCache[origStyleName];
            const origBlock = styleBlocks[origStyleName] || '';
            const newName = `cf${filledStyleCounter++}`;
            newStyleDefs.push(makeFilledStyle(origBlock, newName));
            filledStyleCache[origStyleName] = newName;
            return newName;
        }

        // Helper: build a text:p element
        const tp = (text) => `<text:p>${text.replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;')}</text:p>`;

        // ── Bold the month title and flank it with "LIVE MUSIC NIGHTLY / ON
        // THE PATIO" text, styled to match the title's own color and font —
        // pulled from the template's real style block, not a guessed hex
        // value.
        //
        // IMPORTANT (found via isolated test, not guesswork): a draw:frame's
        // text wraps based on its HOST CELL's actual column width, not its
        // own svg:width. The old image-based version put the right-side
        // logo in a single narrow covered-cell (~0.67in wide) split out of
        // the 13-column run — that was fine for a PNG (images don't wrap),
        // but breaks for real text, forcing an extra line. The fix: anchor
        // BOTH header-text frames inside the same wide 14-column title cell
        // that already holds the month title, and position the right one
        // via an svg:x offset computed from the template's real column
        // widths (not a hardcoded coordinate).
        {
            const headerRow = allRows[monthRowIdx];
            const titleCellMatch = headerRow.match(/<table:table-cell([^>]*)>(<text:p>[\s\S]*?<\/text:p>)<\/table:table-cell>/);

            if (titleCellMatch) {
                const titleAttrs = titleCellMatch[1];
                const titleText  = titleCellMatch[2];

                const styleNameMatch = titleAttrs.match(/table:style-name="([^"]+)"/);
                let boldTitleAttrs = titleAttrs;
                let headerColorAttr = '';   // fo:color="..." pulled from the real title style, if present
                let headerFontName  = 'Arial';
                // The tags use their own font (Verdana, bold) — deliberately
                // different from the title's font per Mike's request, but a
                // clean complementary sans-serif rather than a clashing one.
                const flankFontName = 'Verdana';

                // Shared sizing constants — same font family for title and
                // flanking text, so the same empirical char-width factor
                // applies to both.
                const avgCharWidthPerPt = 0.00885; // empirical, Arial Bold caps/digits — recalibrated from a real rendered file at 38pt (title measured 4.71in for 14 chars, vs the earlier 0.0096 constant — measured at 48pt — predicting 5.11in); char width isn't perfectly linear across sizes, so this is calibrated at the size actually in use now
                const titleChars = `${monthUpper} ${year}`.length;
                const safeWidthIn = 6.3; // true centered-title clearance, symmetric layout
                let scaledFontPt = Math.floor(safeWidthIn / (titleChars * avgCharWidthPerPt));
                // Capped at 38pt (was 48pt) — a deliberate trade-off: the title
                // stays clearly the dominant element, but leaves meaningfully
                // more room on both sides for the "LIVE MUSIC NIGHTLY" tags,
                // which was the actual ask (bigger tags, every month).
                scaledFontPt = Math.max(26, Math.min(38, scaledFontPt));
                const titleRenderedWidthIn = titleChars * scaledFontPt * avgCharWidthPerPt;

                if (styleNameMatch) {
                    const origBlock = styleBlocks[styleNameMatch[1]] || '';
                    const boldName = `cfBold${filledStyleCounter++}`;

                    // Pull the title's actual color/font straight out of the
                    // template so the flanking text always matches it exactly
                    // (a complementary blue is easiest to guarantee by just
                    // reusing the same color).
                    const colorMatch = origBlock.match(/fo:color="([^"]+)"/);
                    if (colorMatch) headerColorAttr = colorMatch[1];
                    const fontMatch = origBlock.match(/style:font-name="([^"]+)"/);
                    if (fontMatch) headerFontName = fontMatch[1];

                    let newTitleStyleBlock = origBlock
                        .replace(/style:name="[^"]+"/, `style:name="${boldName}"`)
                        .replace(/fo:font-weight="normal"/g, 'fo:font-weight="bold"')
                        .replace(/fo:font-size="[\d.]+pt"/g, `fo:font-size="${scaledFontPt}pt"`)
                        .replace(/style:font-size-asian="[\d.]+pt"/g, `style:font-size-asian="${scaledFontPt}pt"`)
                        .replace(/style:font-size-complex="[\d.]+pt"/g, `style:font-size-complex="${scaledFontPt}pt"`);

                    newStyleDefs.push(newTitleStyleBlock);
                    boldTitleAttrs = titleAttrs.replace(/table:style-name="[^"]+"/, `table:style-name="${boldName}"`);
                }

                // Compute the title cell's REAL total width by summing the
                // actual widths of the 14 columns it spans, read from the
                // template's own table:table-column / style:column-width
                // definitions — not assumed.
                const colSpanMatch = titleAttrs.match(/table:number-columns-spanned="(\d+)"/);
                const colSpan = colSpanMatch ? parseInt(colSpanMatch[1], 10) : 14;

                const columnWidthStyles = {};
                const colStyleRe = /<style:style style:name="(co\d+)" style:family="table-column"[^>]*>[\s\S]*?style:column-width="([\d.]+)in"/g;
                let cwm;
                while ((cwm = colStyleRe.exec(content)) !== null) {
                    columnWidthStyles[cwm[1]] = parseFloat(cwm[2]);
                }
                // Expand the template's <table:table-column> sequence (with
                // repeats) into a flat per-column style-name list.
                const colDefs = [];
                const colDefRe = /<table:table-column table:style-name="(co\d+)"(?:[^>]*table:number-columns-repeated="(\d+)")?[^>]*\/>/g;
                let cdm;
                while ((cdm = colDefRe.exec(content)) !== null) {
                    const repeat = cdm[2] ? parseInt(cdm[2], 10) : 1;
                    for (let i = 0; i < repeat; i++) colDefs.push(cdm[1]);
                }
                let titleTotalWidthIn = 0;
                for (let i = 0; i < colSpan && i < colDefs.length; i++) {
                    titleTotalWidthIn += columnWidthStyles[colDefs[i]] || 0;
                }
                // Fallback if column data wasn't found for some reason —
                // keeps a known-good approximate total rather than
                // positioning off-screen.
                if (titleTotalWidthIn <= 0) titleTotalWidthIn = 9.94;

                // ── Dynamic flanking-text sizing ────────────────────────────
                // Mike's spec: the "LIVE MUSIC NIGHTLY / ON THE PATIO" tags
                // should grow or shrink to fit whatever room is actually left
                // on each side of the title — bigger for a short month name
                // (e.g. MAY), smaller for a long one (e.g. SEPTEMBER) — same
                // proportional feel as the hand-made May reference.
                //
                // IMPORTANT (found by inspecting the real file, not guessed):
                // the "gr1" graphic style — originally built for the old
                // image-based logos — carries its own internal padding
                // (fo:padding-left/right, ~0.0984in each side in the real
                // template) plus fo:wrap-option="wrap", which eats into the
                // frame's usable text width from the inside. Fix: don't
                // reuse gr1 — build our own graphic style with small,
                // explicit padding instead.
                //
                // ALSO IMPORTANT (found by direct measurement, not guessed):
                // an earlier version picked flankFontPt conservatively using
                // a "buffer," but then built the frame's actual width from
                // the font size alone — the buffer influenced which font
                // size got chosen but was never actually reserved as real
                // space in the final layout, so titles and tags still ended
                // up touching for long month names. Fixed by computing the
                // title's real left/right edges directly and guaranteeing
                // bufferIn of physical clearance from those edges by
                // construction, not by hoping a rounding step leaves enough.
                const framePadLeftIn = 0.02, framePadRightIn = 0.02;
                const framePaddingTotalIn = framePadLeftIn + framePadRightIn;

                const marginIn = 0.15;   // frame's distance from the cell's outer edge
                const bufferIn = 0.22;   // guaranteed clearance between title edge and frame edge (raised to offset the 8% frame-width safety margin, which otherwise ate into this)

                const titleCenterIn = titleTotalWidthIn / 2;
                const titleHalfWidthIn = titleRenderedWidthIn / 2;
                // Room available for EACH side's frame, with margin (outer)
                // and buffer (inner, next to the title) already subtracted —
                // whatever frame we build to fit inside this is guaranteed
                // not to touch the title or run off the cell edge.
                const sideRoomIn = Math.max(1.0, titleCenterIn - titleHalfWidthIn - bufferIn - marginIn);
                const usableTextWidthIn = Math.max(0.5, sideRoomIn - framePaddingTotalIn);
                const flankLongestLineChars = 'LIVE MUSIC NIGHTLY'.length;
                // Verdana renders noticeably wider than Arial at the same
                // point size (it was designed for screen legibility with
                // wider letterforms) — the shared avgCharWidthPerPt constant
                // was calibrated on Arial Bold, so using it unmodified here
                // would undersize the safety margin and risk reopening the
                // exact overlap bug already fixed. Applying a conservative
                // ~15% width bump for this specific calculation until it's
                // verified against a real render.
                // Measured directly from a real rendered file: Verdana Bold's
                // actual ink width came out to 0.00837 in/pt/char (18-char
                // line measured at 1.807in @ 12pt) — narrower than assumed,
                // not wider. The earlier 15% "Verdana is wider" bump was based
                // on general font knowledge rather than this specific render;
                // real measurement overrides it.
                const avgCharWidthPerPtFlank = 0.00837;
                let flankFontPt = Math.floor(usableTextWidthIn / (flankLongestLineChars * avgCharWidthPerPtFlank));
                flankFontPt = Math.max(9, Math.min(26, flankFontPt));
                // Built from the chosen font size alone — guaranteed <= sideRoomIn,
                // so the clearance computed above is real, not assumed.
                // 8% safety margin: building the frame as an EXACT fit to the
                // predicted text width (no slack) risks clipping instead of
                // wrapping if the real render differs even slightly from the
                // predicted width — confirmed by a real test showing narrower
                // ink at a bigger font size than at a smaller one, a sign of
                // truncation, not measurement noise.
                const frameWidthIn = (flankLongestLineChars * flankFontPt * avgCharWidthPerPtFlank * 1.08 + framePaddingTotalIn).toFixed(4);
                const frameHeightIn = (2 * flankFontPt / 72 * 1.35 + framePadLeftIn).toFixed(4); // 2 lines, ~1.35x line-height, plus top/bottom-ish padding allowance

                // Our own tight graphic style — same visual behavior as gr1
                // (centered text, no border/fill) but with padding we
                // control and already accounted for above, rather than
                // inheriting the image frame's padding implicitly.
                const headerFrameStyleName = `cfHeaderFrame${filledStyleCounter}`;
                newStyleDefs.push(
                    `<style:style style:name="${headerFrameStyleName}" style:family="graphic">` +
                    `<style:graphic-properties draw:stroke="none" draw:fill="none" draw:textarea-horizontal-align="center" draw:textarea-vertical-align="middle" draw:auto-grow-height="false" draw:auto-grow-width="false" fo:padding-top="${framePadLeftIn}in" fo:padding-bottom="${framePadLeftIn}in" fo:padding-left="${framePadLeftIn}in" fo:padding-right="${framePadRightIn}in" fo:wrap-option="wrap"/>` +
                    `</style:style>`
                );

                // Paragraph style for the flanking header text: bold, matching
                // the title's color/font, sized dynamically per above.
                const headerTextStyleName = `cfHeaderTxt${filledStyleCounter++}`;
                const headerColorProp = headerColorAttr ? ` fo:color="${headerColorAttr}"` : '';
                newStyleDefs.push(
                    `<style:style style:name="${headerTextStyleName}" style:family="paragraph">` +
                    `<style:paragraph-properties fo:text-align="center" fo:margin="0in"/>` +
                    `<style:text-properties fo:font-weight="bold" fo:font-size="${flankFontPt}pt" style:font-name="${flankFontName}"${headerColorProp} style:font-weight-asian="bold" style:font-size-asian="${flankFontPt}pt" style:font-weight-complex="bold" style:font-size-complex="${flankFontPt}pt"/>` +
                    `</style:style>`
                );
                const headerLines =
                    `<text:p text:style-name="${headerTextStyleName}">LIVE MUSIC NIGHTLY</text:p>` +
                    `<text:p text:style-name="${headerTextStyleName}">ON THE PATIO</text:p>`;

                // Left frame anchored at the fixed outer margin; right frame
                // mirrors it from the cell's right edge. Both are guaranteed
                // (by the sideRoomIn construction above) to stay bufferIn
                // clear of the title on their inner edge.
                const rightX = (titleTotalWidthIn - marginIn - parseFloat(frameWidthIn)).toFixed(4);

                // Both frames live inside the SAME wide title cell (a
                // draw:frame's text wraps to its HOST CELL's column width,
                // not its own svg:width — confirmed via isolated test — so
                // anchoring both in this one wide cell avoids the old
                // narrow-covered-cell wrap bug entirely).
                const leftHeaderText  = `<draw:frame draw:z-index="0" draw:name="HeaderTextLeft" draw:style-name="${headerFrameStyleName}" draw:text-style-name="P1" svg:width="${frameWidthIn}in" svg:height="${frameHeightIn}in" svg:x="${marginIn}in" svg:y="0.17in"><draw:text-box>${headerLines}</draw:text-box></draw:frame>`;
                const rightHeaderText = `<draw:frame draw:z-index="1" draw:name="HeaderTextRight" draw:style-name="${headerFrameStyleName}" draw:text-style-name="P1" svg:width="${frameWidthIn}in" svg:height="${frameHeightIn}in" svg:x="${rightX}in" svg:y="0.17in"><draw:text-box>${headerLines}</draw:text-box></draw:frame>`;

                const newTitleCell = `<table:table-cell${boldTitleAttrs}>${titleText}${leftHeaderText}${rightHeaderText}</table:table-cell>`;

                allRows[monthRowIdx] = headerRow.replace(titleCellMatch[0], newTitleCell);
            }
        }

        // Helper: inject text into a row's cells by day-of-week column index.
        // IMPORTANT: the ODS template uses TWO real <table:table-cell> elements
        // per day-of-week in the date row (a date-number cell + an adjacent
        // secondary cell), but only ONE real <table:table-cell> per day-of-week
        // in the content rows (the second half of each merged block is a
        // <table:covered-table-cell>, a different element that must not be
        // touched). cellsPerDow lets the same function handle both cases.
        function injectRowText(templateRow, textByDow, cellsPerDow = 1, useFilledStyle = false) {
            // Match a real table-cell as ONE atomic unit, whether it's self-closing
            // (<table:table-cell .../>) or has separate open/content/close tags.
            // Self-closing is tried first so it can't be mistaken for an opening
            // tag that then swallows unrelated cells further down the row.
            const cellRe = /<table:table-cell\b[^>]*?\/>|<table:table-cell\b[^>]*?>[\s\S]*?<\/table:table-cell>/g;
            const cellMatches = [];
            let cm;
            while ((cm = cellRe.exec(templateRow)) !== null) {
                cellMatches.push({ index: cm.index, full: cm[0] });
            }

            let out = templateRow;
            // Work backwards so earlier replacements don't shift later indices
            for (let col = 6; col >= 0; col--) {
                if (textByDow[col] === undefined) continue;
                const cellIdx = col * cellsPerDow;
                const match = cellMatches[cellIdx];
                if (match === undefined) continue;

                const attrMatch = match.full.match(/^<table:table-cell([^>]*?)\/?>/);
                let attrs = attrMatch ? attrMatch[1] : '';
                if (useFilledStyle) {
                    const styleNameMatch = attrs.match(/table:style-name="([^"]+)"/);
                    if (styleNameMatch) {
                        const filledName = getFilledStyleName(styleNameMatch[1]);
                        attrs = attrs.replace(/table:style-name="[^"]+"/, `table:style-name="${filledName}"`);
                    }
                }
                const newCell = `<table:table-cell${attrs}>${tp(textByDow[col])}</table:table-cell>`;
                out = out.substring(0, match.index) + newCell + out.substring(match.index + match.full.length);
            }
            return out;
        }

        // The week structure in ODS (after month header row and day-header row):
        // For each week: 1 date row (ro2/ro3) + 5 content rows (ro3/ro4)
        // Month header = monthRowIdx, day header = monthRowIdx+1
        // Week 1 starts at monthRowIdx+2

        const newRows = [...allRows];
        let weekCount = 0;

        for (let w = 0; w < weekData.length; w++) {
            const week = weekData[w];
            if (Object.values(week).every(d => d.dateNum === null)) break;

            const baseIdx = monthRowIdx + 2 + w * 6; // date row for this week

            // Row 0: date numbers + holidays
            const dateTextByDow = {};
            for (let dow = 0; dow < 7; dow++) {
                if (week[dow].dateNum) {
                    dateTextByDow[dow] = String(week[dow].dateNum);
                }
            }
            newRows[baseIdx] = injectRowText(newRows[baseIdx], dateTextByDow, 2);

            // Rows 1-5: content lines
            for (let r = 1; r <= 5; r++) {
                const lineTextByDow = {};
                for (let dow = 0; dow < 7; dow++) {
                    const line = week[dow].lines[r - 1];
                    if (line) lineTextByDow[dow] = line;
                }
                newRows[baseIdx + r] = injectRowText(newRows[baseIdx + r], lineTextByDow, 1, true);
            }

            weekCount = w + 1;
        }

        // ── 6. Extract only this month's rows (trim to just current month) ────
        const monthStart = monthRowIdx;
        const monthEnd   = monthRowIdx + 1 + weekCount * 6; // header + day row + weeks

        // Build minimal ODS with just this month
        // Get the ODS structure (everything outside the table rows)
        //
        // IMPORTANT: this must capture the table's opening tag AND every
        // <table:table-column> width definition that follows it in the
        // template — those are NOT <table:table-row> elements, so the row
        // extraction above never touches them. Previously only the bare
        // opening tag was kept and all column-width definitions were
        // silently dropped, which made every column fall back to one
        // uniform default width. That's invisible for most cells but
        // squeezes any word-wrapped text placed in a genuinely narrow
        // column (e.g. the single covered-cell hosting the right-side
        // header text) into extra line breaks it shouldn't need.
        const tableOpenMatch  = content.match(/<table:table[^>]*>[\s\S]*?(?=<table:table-row)/);
        const tableCloseMatch = content.match(/<\/table:table>/);

        // Rebuild content.xml with only the month's rows
        const selectedRows = newRows.slice(monthStart, monthEnd + 1).join('');
        const beforeTable  = content.substring(0, content.indexOf('<table:table'));
        const afterTable   = content.substring(content.lastIndexOf('</table:table>') + '</table:table>'.length);
        const tableOpen    = tableOpenMatch ? tableOpenMatch[0] : '<table:table>';

        // Insert the generated "filled" cell styles into automatic-styles so
        // the style names referenced by our injected cells actually resolve.
        let beforeTableWithStyles = beforeTable;
        const stylesInsertPoint = beforeTable.lastIndexOf('</office:automatic-styles>');
        if (stylesInsertPoint !== -1 && newStyleDefs.length > 0) {
            beforeTableWithStyles =
                beforeTable.substring(0, stylesInsertPoint) +
                newStyleDefs.join('') +
                beforeTable.substring(stylesInsertPoint);
        }

        const newContent = beforeTableWithStyles +
            tableOpen +
            selectedRows +
            '</table:table>' +
            afterTable;

        zip.file('content.xml', newContent);
        // Note: manifest.xml is left untouched now — no Pictures/ entries to add
        // since the header is native text, not embedded images.

        // ── 7. Download ───────────────────────────────────────────────────────
        const blob     = await zip.generateAsync({ type: 'blob', compression: 'DEFLATE' });
        const filename = `Exchange Booking Calendar-${monthName} ${year} V1.ods`;
        const url      = URL.createObjectURL(blob);
        const a        = document.createElement('a');
        a.href = url; a.download = filename;
        document.body.appendChild(a); a.click();
        document.body.removeChild(a); URL.revokeObjectURL(url);
    }

    return (
        <>
            <style jsx>{`
                .fc-border { color: #E0F2FF; }
                .fc-day-sat, .fc-day-sun { background-color: #E0F2FF; }
                .fc-col-header-cell { background-color: #264a81; font-size: 1.7vw; }
                .fc-col-header-cell-cushion { color: #ffffff; }
                .fc-event-title, .fc-event .fc-title { white-space:normal; overflow:hidden; line-height:1; justify-content:center; text-align:center; overflow:visible; overflow-wrap:anywhere; }
                .fc-daygrid-day.fc-day-other .fc-event { display:none; float:left; }
                .fc-daygrid-day-number { float:left; margin-right:auto; font-size:1.5vw; color:black; font-weight:bold; }
                .fc-event { display:flex; align-items:center; justify-content:center; text-align:center; overflow:hidden; overflow-wrap:anywhere; white-space:initial; }
                .fc-toolbar-title { font-size:6vw !important; color:#264a81; }
                .fc-timegrid-slot-minor { border-bottom:1px solid var(--fc-border-color,black) !important; border-top:1px dotted var(--fc-border-color,black) !important; }
            `}</style>

            <div className='content'>
                <div className='calendarButtons' style={{ paddingLeft:'30px', paddingTop:'15px' }}>
                    <Button style={{ paddingRight:'30px' }} startIcon={<KeyboardDoubleArrowLeftIcon />} onClick={() => navigate('/venues')}>
                        Return to Venues
                    </Button>
                    <Button style={{ marginLeft:'10px' }} variant="contained" endIcon={<DownloadIcon />} onClick={() => generateODS()}>
                        Download Filled Calendar (ODS)
                    </Button>
                </div>

                <div id="calendar">
                    <div className="calendar-container">
                        <FullCalendar
                            ref={calendarRef} events={events}
                            headerToolbar={{ center:'title', right:'prev,next today', left:'prev,next today' }}
                            eventContent={({ event }) => (
                                <div style={{ display:'flex', flexDirection:'column', alignItems:'center', height:'100%' }}>
                                    <strong style={{ fontSize:'1.45vw', color:'black', textAlign:'center' }}>{formatEventTitle(event.title)}</strong>
                                    <i style={{ fontSize:'1.35vw', fontWeight:'bold', color:'black' }}>
                                        {to12HourFormat(event.extendedProps.startTime)} {'\u2013'} {to12HourFormat(event.extendedProps.endTime)}
                                    </i>
                                </div>
                            )}
                            dayHeaderFormat={{ weekday:'long' }} contentHeight="auto"
                            plugins={[dayGridPlugin, interactionPlugin]} initialView="dayGridMonth"
                            dateClick={handleDateClick} eventClick={handleEventClick}
                            dayCellContent={renderCopyButton} showNonCurrentDates={false}
                        />
                    </div>
                </div>

                <AddEditEvent open={openForm} setOpen={setOpenForm} formType={formType}
                    event={currentEvent} onDownloadCalendar={generateODS} />

                <Snackbar open={copied} autoHideDuration={3000} onClose={handleCopiedClosed}>
                    <Alert onClose={handleCopiedClosed} severity="success" sx={{ width:'100%' }}>Copied Text!</Alert>
                </Snackbar>
            </div>
        </>
    );
}

export default Calendar;
